import { useCallback, useEffect, useState } from "react";
import { API } from "../../services/api";

const STATUS = {
    SUCCESS: { label: "Import réussi", color: "#0C3B3C", background: "#DCFCE7", dot: "#17B978" },
    FAILED: { label: "Échec du dernier import", color: "#991B1B", background: "#FEE2E2", dot: "#DC2626" },
    RUNNING: { label: "Import en cours", color: "#854D0E", background: "#FEF3C7", dot: "#C8A44D" },
};

function formatDate(value) {
    if (!value) return "Aucune";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Date inconnue";
    return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function formatCount(value) {
    return Number.isFinite(Number(value)) ? Number(value).toLocaleString("fr-FR") : "—";
}

function Metric({ label, value }) {
    return <div style={{ padding: "12px 14px", borderRadius: "9px", background: "#F4F1EA", border: "1px solid #E5E0D5" }}>
        <span style={{ display: "block", color: "#5A6D68", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "5px" }}>{label}</span>
        <span style={{ display: "block", color: "#122A26", fontSize: "14px", fontWeight: 700 }}>{value}</span>
    </div>;
}

export function BdpmImportStatus() {
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await API.get("/medicaments-reference/info");
            setInfo(data);
        } catch {
            setError("Impossible de récupérer l’état du dernier import BDPM.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const status = STATUS[info?.status] ?? {
        label: "Aucun import enregistré", color: "#5A6D68", background: "#F4F1EA", dot: "#5A6D68",
    };

    return <section aria-labelledby="bdpm-import-title" aria-live="polite" style={{ background: "white", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", padding: "28px", marginBottom: "20px", borderTop: "4px solid #0C3B3C" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", marginBottom: "20px" }}>
            <div>
                <h2 id="bdpm-import-title" style={{ color: "#0C3B3C", margin: "0 0 5px", fontSize: "16px" }}>Catalogue médicaments BDPM</h2>
                <p style={{ margin: 0, color: "#5A6D68", fontSize: "13px" }}>État de l’alimentation commune des spécialités et de leurs présentations CIP13.</p>
            </div>
            <button type="button" onClick={refresh} disabled={loading} aria-label="Actualiser l’état de l’import BDPM" style={{ padding: "8px 13px", borderRadius: "8px", background: "white", color: "#0C3B3C", border: "1px solid #0C3B3C", opacity: loading ? 0.6 : 1 }}>
                {loading ? "Actualisation…" : "Actualiser"}
            </button>
        </div>
        {loading && !info && <p style={{ color: "#5A6D68", margin: 0 }}>Chargement de l’état de l’import…</p>}
        {error && <p role="alert" style={{ color: "#991B1B", margin: info ? "0 0 16px" : 0 }}>{error}</p>}
        {(!loading || info) && info && <>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px", marginBottom: "18px" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "7px", padding: "6px 10px", borderRadius: "999px", color: status.color, background: status.background, fontSize: "13px", fontWeight: 700 }}>
                    <span aria-hidden="true" style={{ width: "8px", height: "8px", borderRadius: "50%", background: status.dot }} />{status.label}
                </span>
                {info.status === "RUNNING" && <span style={{ color: "#5A6D68", fontSize: "12px" }}>Les données validées restent disponibles pendant l’import.</span>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(165px, 1fr))", gap: "12px" }}>
                <Metric label="Dernier import réussi" value={formatDate(info.lastSyncAt)} />
                <Metric label="Dernière tentative" value={formatDate(info.lastStartedAt)} />
                <Metric label="Spécialités (CIS)" value={formatCount(info.specialtyCount)} />
                <Metric label="Présentations (CIP13)" value={formatCount(info.presentationCount)} />
            </div>
            {info.status === "FAILED" && info.lastError && <div style={{ marginTop: "16px", padding: "10px 12px", borderRadius: "8px", background: "#FEF2F2", border: "1px solid #FECACA" }}>
                <span style={{ display: "block", color: "#991B1B", fontSize: "12px", fontWeight: 700, marginBottom: "3px" }}>Détail de l’échec</span>
                <span style={{ color: "#7F1D1D", fontSize: "13px", overflowWrap: "anywhere" }}>{info.lastError}</span>
            </div>}
        </>}
    </section>;
}
